/**
 * TopBar — 상단 바 (Shell 고정, C-1 기준)
 *
 * 브레드크럼은 우선 TM_MENU 조회 트리(resolveMenuCrumb, 스튜디오_개선_설계서_v3.0 §9
 * FR-29)를 참조하고, 거기 없는 경로만 AGENT_MENU_CONFIG 자동 생성이나 STATIC_CRUMBS/
 * STATIC_TAIL(TM_MENU에 없는 말단 화면명 전용)로 보완한다.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useAppStore } from '@indipia/shared';
import { AGENT_MENU_CONFIG } from '../../registry/menu-config';
import { resolveNavOwner, resolveScreenName } from '../../registry/nav-mapping';
import {
  useMenuTree, findByCode, pickByPosition, resolveMenuCrumb, resolveMenuCrumbPrefix, toVisibleTree,
  isRouteOrDescendant, type MenuNode,
} from '../../registry/menu-tree';
import { openHelp } from '../../shared/lib/help-topic';
import { useNotifications } from './notifications/NotificationProvider';
import { MenuIcon } from './LeftNav';

// TM_MENU에 없는 경로(테스트/개발자 메뉴, 탈퇴 화면 등) 전용 — 위치가 완전히 독립적이라
// resolveMenuCrumb/Prefix로 도출할 수 없는 것만 남긴다(FR-29, §5.2).
const STATIC_CRUMBS: Record<string, string[]> = {
  '/dev-menu':               ['스튜디오', '개발자 메뉴'], // 테스트 관련 코드, 추후 삭제 예정
  '/test':                   ['스튜디오', '개발자 가이드'],
  '/agents':                 ['스튜디오', '에이전트 전체보기'],
  '/showroom':               ['고객 상담관리'],
  '/settings/withdraw/master':   ['내 계정', '내 정보', '마스터 탈퇴'],
  '/settings/withdraw/staff':    ['내 계정', '내 정보', '직원 탈퇴'],
};

// TM_MENU 노드가 앞부분(그룹/플라이아웃 › 항목명)까지는 커버하지만, 그 라우트에 딸린
// 세부 화면(신규 등록/수정/상세 등)은 TM_MENU에 없는 말단 명칭이라 여기서 이어붙인다.
const STATIC_TAIL: Record<string, string> = {
  '/showroom/inquiries': '상담신청관리',
  '/todo/week':          '이번 주',
  '/partners/new':       '신규 등록',
  '/vendors/new':        '신규 등록',
};

function matchMenuPath(menuPath: string, pathname: string, search: string): boolean {
  const [mp, mq] = menuPath.split('?');
  if (pathname !== mp) return false;
  if (mq) return search === `?${mq}`;
  return !search; // 메뉴에 query 없으면 현재 search도 없어야 매칭
}

function getBreadcrumb(tree: MenuNode[] | undefined, pathname: string, search: string): string[] {
  const menuCrumb = resolveMenuCrumb(tree, pathname, search);
  if (menuCrumb) return menuCrumb;

  if (STATIC_CRUMBS[pathname]) return STATIC_CRUMBS[pathname];

  if (STATIC_TAIL[pathname]) {
    const prefix = resolveMenuCrumbPrefix(tree, pathname) ?? [];
    return [...prefix, STATIC_TAIL[pathname]];
  }

  // 소속 매핑(nav-mapping.ts) — 인디포토/인디플래너/인디포트폴리오는 진입 맥락과 무관하게
  // 라우트 앞부분만으로 좌측 내비 라벨("나의 사진첩" 등)을 그대로 1레벨 크럼으로 쓴다.
  const owner = resolveNavOwner(pathname);
  if (owner) {
    const screen = resolveScreenName(pathname, search);
    return screen ? [owner.ownerLabel, screen] : [owner.ownerLabel];
  }

  // 동적 경로 패턴 — TM_MENU에 없는 말단만 붙이고, 앞부분은 접두 일치로 가져온다.
  if (/^\/partners\/\d+\/edit$/.test(pathname)) {
    return [...(resolveMenuCrumbPrefix(tree, pathname) ?? ['스튜디오', '나의 시공파트너']), '수정'];
  }
  if (/^\/vendors\/\d+\/edit$/.test(pathname)) {
    return [...(resolveMenuCrumbPrefix(tree, pathname) ?? ['스튜디오', '나의 자재파트너']), '수정'];
  }
  if (/^\/cs\/inquiry\/\d+$/.test(pathname)) {
    return [...(resolveMenuCrumbPrefix(tree, pathname) ?? ['고객센터', '1:1 문의하기']), '문의 상세'];
  }
  if (/^\/agents\/work-log\/workorder\/\d+$/.test(pathname)) {
    return ['인디공사현황', '작업요청', '작업요청 상세조회'];
  }
  if (/^\/agents\/work-log\/field-reports\/\d+$/.test(pathname)) {
    return ['인디공사현황', '현장보고', '현장보고 상세조회'];
  }

  const agent = AGENT_MENU_CONFIG.find((a) => pathname.startsWith(a.basePath));
  if (!agent) return ['스튜디오'];

  for (const item of agent.children) {
    if (item.children) {
      for (const sub of item.children) {
        if (matchMenuPath(sub.path, pathname, search)) {
          return [agent.label, item.label, sub.label];
        }
      }
    } else {
      if (matchMenuPath(item.path, pathname, search)) {
        return [agent.label, item.label];
      }
    }
  }

  return [agent.label];
}

// ── 상단바 우측 메뉴(TM_MENU MENU_POSITION_CODE=RIGHT_TOP) ─────────────────────────
// 위치·순서·하위 메뉴·아이콘(ICON_KEY)은 전부 DB가 정한다. 코드에 남은 것은 안 읽은 알림 배지뿐이다.

/** 안 읽은 알림 수 배지를 붙이는 메뉴 */
const UNREAD_BADGE_CODE = 'NOTIFICATION';

/** 하위 메뉴(displayYn 무관, 원본 트리) 중 현재 화면 또는 그 세부 화면이 있으면 활성 */
function hasActiveRoute(node: MenuNode | null, pathname: string, search: string): boolean {
  if (!node) return false;
  return node.children.some(
    (c) => (c.routePath && isRouteOrDescendant(c.routePath, pathname, search)) || hasActiveRoute(c, pathname, search),
  );
}

const dropItemCls = (isActive: boolean) =>
  `block px-3.5 py-2 text-sm ${isActive ? 'font-medium text-text' : 'text-text-secondary hover:bg-surface-subtle hover:text-text'}`;

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-medium leading-none text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** 이동형(LINK) 상단 메뉴 — 아이콘 버튼 클릭 시 routePath로 이동 */
function MenuLinkButton({ node, icon, active, badge = 0 }: { node: MenuNode; icon: React.ReactNode; active: boolean; badge?: number }) {
  return (
    <Link
      to={node.routePath ?? '#'}
      title={node.name}
      aria-label={node.name}
      className={`relative flex h-8 w-8 items-center justify-center rounded-md ${
        active ? 'bg-surface-subtle text-text' : 'text-text-muted hover:bg-surface-subtle hover:text-text'
      }`}
    >
      {icon}
      <UnreadBadge count={badge} />
    </Link>
  );
}

// 하위 메뉴가 있는 상단 메뉴 — 아이콘 버튼 클릭 시 TM_MENU 하위 항목을 드롭다운으로 펼친다.
// GROUP_HEADER는 소제목, COLLAPSE_GROUP은 소제목 + 하위 항목으로 펼쳐 보여준다.
function MenuDropdownButton({
  label, icon, items, active, badge = 0,
}: {
  label: string;
  icon: React.ReactNode;
  items: MenuNode[];
  active: boolean;
  badge?: number;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 라우트 변경 시(메뉴 항목 클릭 등) 드롭다운 닫기
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={label}
        aria-label={label}
        className={`relative flex h-8 w-8 items-center justify-center rounded-md ${
          active || open ? 'bg-surface-subtle text-text' : 'text-text-muted hover:bg-surface-subtle hover:text-text'
        }`}
      >
        {icon}
        <UnreadBadge count={badge} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-44 rounded-md border border-border bg-surface-raised py-1.5 shadow-xl">
          {items.map((item) =>
            item.typeCode === 'GROUP_HEADER' || item.typeCode === 'COLLAPSE_GROUP' ? (
              <div key={item.code}>
                <div className="px-3.5 pb-1 pt-2 text-xs text-text-muted">{item.name}</div>
                {item.children.map((sub) => (
                  <NavLink key={sub.code} to={sub.routePath ?? '#'} className={({ isActive }) => `${dropItemCls(isActive)} pl-5`}>
                    {sub.name}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink key={item.code} to={item.routePath ?? '#'} className={({ isActive }) => dropItemCls(isActive)}>
                {item.name}
              </NavLink>
            ),
          )}
        </div>
      )}
    </div>
  );
}

export function TopBar() {
  const { pathname, search } = useLocation();
  const breadcrumbOverride = useAppStore((s) => s.breadcrumbOverride);
  // LeftNav와 같은 queryKey(['menus','WEB_STUDIO'])를 써서 캐시를 공유한다 — 별도 요청 없음.
  const { data: menuTree } = useMenuTree();
  // 상단바 우측 메뉴 = RIGHT_TOP 최상위 노드(SORT_ORDER 순). 그리는 목록은 displayYn='N'(라우팅 전용)을
  // 걷어낸 트리로, 활성 판정은 원본 트리(menuTree)로 한다 — 상세 화면에서도 활성 유지.
  const rightTopNodes = pickByPosition(toVisibleTree(menuTree), 'RIGHT_TOP');
  const { unreadCount } = useNotifications();
  // 브레드크럼은 상세/드릴다운(displayYn='N') 화면도 표시해야 하므로 raw tree(menuTree)를 그대로 쓴다.
  // 라우트 이동 없이 열리는 오버레이(SiteWorkspaceDrawer 등)가 켜둔 임시 브레드크럼(FR-16) —
  // 없으면 라우트 기반 기본 계산으로 폴백.
  const crumbs = breadcrumbOverride ?? getBreadcrumb(menuTree, pathname, search);

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface-raised px-5">
      {/* 좌: 브레드크럼 (최대 3레벨) */}
      <nav aria-label="현재 위치" className="flex items-center gap-1.5 text-sm">
        {crumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && (
              <svg
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className="shrink-0 text-text-muted"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
            <span className={i === crumbs.length - 1 ? 'font-medium text-text' : 'text-text-muted'}>
              {crumb}
            </span>
          </span>
        ))}
      </nav>

      {/* 우: 테스트 진입 버튼 · 도움말 · RIGHT_TOP 메뉴(TM_MENU, SORT_ORDER 순) */}
      <div className="flex items-center gap-1">
        {/* ▼▼▼ 테스트 관련 코드, 추후 삭제 예정 — 이 if 블록 전체를 지우면 됨 (테스트/약관확인/가이드확인/UI표준 허브 진입 버튼).
             도움말 버튼 왼쪽에 놓여, 삭제해도 도움말은 제 위치(오른쪽 끝)를 유지한다. */}
        {pathname === '/app' && (
          <Link
            to="/dev-menu"
            title="개발자 메뉴"
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted/30 hover:bg-surface-subtle hover:text-text-muted"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
          </Link>
        )}
        {/* ▲▲▲ 테스트 관련 코드 끝 */}
        {/* 도움말 — 지금 보고 있는 페이지에 맞는 가이드를 새 창으로 연다 */}
        <button
          type="button"
          onClick={() => openHelp(pathname)}
          title="도움말"
          aria-label="도움말"
          className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-surface-subtle hover:text-text"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>
        {rightTopNodes.map((node) => {
          const icon = <MenuIcon iconKey={node.iconKey} size={18} tint />;
          const badge = node.code === UNREAD_BADGE_CODE ? unreadCount : 0;
          const raw = findByCode(menuTree, node.code);
          if (node.children.length > 0 && node.typeCode !== 'LINK') {
            return (
              <MenuDropdownButton
                key={node.code}
                label={node.name}
                items={node.children}
                active={hasActiveRoute(raw, pathname, search)}
                badge={badge}
                icon={icon}
              />
            );
          }
          return (
            <MenuLinkButton
              key={node.code}
              node={node}
              icon={icon}
              badge={badge}
              active={!!node.routePath && isRouteOrDescendant(node.routePath, pathname, search)}
            />
          );
        })}
      </div>
    </header>
  );
}
